import { REQUEST_SESSION_ID_HEADER } from '@aloft/toolkit-shared';
import { useAuth0 } from '@auth0/auth0-react';
import type { QueryKey, UseQueryOptions } from '@tanstack/react-query';

import { useMemo } from 'react';

import { useSessionID as useSessionIdentifier } from '../useSessionID.js';
import { QueryOperation } from './toolkitComponents.js';

export type ToolkitFetcherConfig = {
  /**
   * Headers to inject in the fetcher
   */
  headers?: {
    authorization?: string;
    [REQUEST_SESSION_ID_HEADER]?: string;
  };
  /**
   * Query params to inject in the fetcher
   */
  queryParams?: Record<string, string>;
  /**
   * Function to get the access token for the request
   */
  accessTokenFn?: () => Promise<string>;
};

export type ToolkitContext = {
  fetcherOptions: ToolkitFetcherConfig;
  queryOptions: {
    /**
     * Set this to `false` to disable automatic refetching when the query mounts or changes query keys.
     * Defaults to `true`.
     */
    enabled?: boolean;
  };
  /**
   * Query key manager.
   */
  queryKeyFn: (operation: QueryOperation) => QueryKey;
};

/**
 * Context injected into every react-query hook wrappers
 *
 * @param queryOptions options from the useQuery wrapper
 */
export function useToolkitContext<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _queryOptions?: Omit<
    UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    'queryKey' | 'queryFn'
  >,
): ToolkitContext {
  const { getAccessTokenSilently } = useAuth0();
  const sessionId = useSessionIdentifier();
  const impersonationUserId = localStorage.getItem('impersonation-user-id');
  return useMemo(
    () => ({
      fetcherOptions: {
        accessTokenFn: async () => await getAccessTokenSilently(),
        headers: {
          [REQUEST_SESSION_ID_HEADER]: sessionId,
          'x-impersonate-user-id': impersonationUserId || undefined,
        },
      },
      queryOptions: {},
      queryKeyFn,
    }),
    [getAccessTokenSilently, sessionId],
  );
}

export const queryKeyFn = (operation: QueryOperation) => {
  const queryKey: unknown[] = hasPathParams(operation)
    ? operation.path
        .split('/')
        .filter(Boolean)
        .map((i) => resolvePathParam(i, operation.variables.pathParams))
    : operation.path.split('/').filter(Boolean);

  if (hasQueryParams(operation)) {
    queryKey.push(operation.variables.queryParams);
  }

  if (hasBody(operation)) {
    queryKey.push(operation.variables.body);
  }

  return queryKey;
};
// Helpers
const resolvePathParam = (key: string, pathParams: Record<string, string>) => {
  if (key.startsWith('{') && key.endsWith('}')) {
    return pathParams[key.slice(1, -1)];
  }
  return key;
};

const hasPathParams = (
  operation: QueryOperation,
): operation is QueryOperation & {
  variables: { pathParams: Record<string, string> };
} => {
  return Boolean((operation.variables as any).pathParams);
};

const hasBody = (
  operation: QueryOperation,
): operation is QueryOperation & {
  variables: { body: Record<string, unknown> };
} => {
  return Boolean((operation.variables as any).body);
};

const hasQueryParams = (
  operation: QueryOperation,
): operation is QueryOperation & {
  variables: { queryParams: Record<string, unknown> };
} => {
  return Boolean((operation.variables as any).queryParams);
};
